import React from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import {
  TrendingDown,
  TrendingUp,
} from '@barracuda-internal/bds-core/dist/Icons/Core';

/**
 * Grouped KPI card — multiple compact tiles inside a single Card,
 * separated by thin vertical dividers. Mirrors the Chaitin admin
 * dashboard's "single card / multiple metrics" pattern.
 *
 * Props:
 *   tiles: [{ label, value, icon?, description?, delta?, accent? }]
 *
 * Tiles are intentionally tight — label (with optional inline icon) on
 * one row, then the value. Description is optional and only renders if
 * supplied; delta chip is rendered inline next to the label if present.
 */

const KpiTile = ({ label, value, icon, description, delta, accent, isFirst }) => {
  const tokens = useTokens();

  // Slim custom delta indicator. BDS Chip even at bdsSize="input" is
  // 20px tall with chunky internal padding — too heavy when sitting
  // next to a value. This pill is ~16px with tighter spacing.
  let deltaPill = null;
  if (delta && Number.isFinite(delta.value) && delta.value !== 0) {
    const up = delta.value > 0;
    const good = up === delta.positiveIsGood;
    const fg = good
      ? tokens.aliasesColorBorderSuccessEmphasis
      : tokens.aliasesColorTextError;
    const bg = good
      ? tokens.aliasesColorBgSuccessSubtle
      : tokens.aliasesColorBgErrorSubtle;
    const Trend = up ? TrendingUp : TrendingDown;
    deltaPill = (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: '2px',
          paddingX: '5px',
          paddingY: '1px',
          backgroundColor: bg,
          borderRadius: tokens.aliasesBorderRadiusDefault,
          color: fg,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        <Trend sx={{ fontSize: 11 }} />
        <Typography
          variant="micro"
          sx={{ color: 'inherit', fontWeight: 600 }}
        >
          {Math.abs(delta.value)}%
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        gap: tokens.aliasesSpaceComponentDense,
        paddingLeft: isFirst ? 0 : tokens.aliasesSpaceContainerDefault,
        borderLeft: isFirst
          ? 'none'
          : `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
      }}
    >
      {/* Label row — gets the full width of the tile so titles like
          "Active accounts" or "Threats blocked" don't compete with the
          delta chip and get clipped. */}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: tokens.aliasesSpaceComponentDense,
          minWidth: 0,
        }}
      >
        {icon && (
          <Box
            sx={{
              color: accent || tokens.aliasesColorTextStaticSubtle,
              display: 'flex',
              fontSize: 14,
              flexShrink: 0,
              '& svg': { width: 14, height: 14 },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="captionSubtle"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
      </Stack>
      {/* Value + delta inline. Value is allowed to ellipsis-trim; the
          delta chip keeps a fixed footprint via flexShrink: 0. */}
      <Stack
        direction="row"
        sx={{
          alignItems: 'baseline',
          gap: tokens.aliasesSpaceComponentDense,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            ...(accent ? { color: accent } : {}),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {value}
        </Typography>
        {deltaPill && (
          <Box sx={{ flexShrink: 0 }}>{deltaPill}</Box>
        )}
      </Stack>
      {description && (
        <Typography
          variant="micro"
          sx={{
            color: tokens.aliasesColorTextStaticSubtle,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {description}
        </Typography>
      )}
    </Stack>
  );
};

const GroupedKpiCard = ({ tiles = [] }) => {
  const tokens = useTokens();
  return (
    <Card
      bdsType="staticSubtle"
      bdsBoxShadow="default"
      bdsBorderRadius="round"
      bdsPadding="default"
      sx={{
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: tokens.aliasesSpaceContainerDefault,
          alignItems: 'stretch',
        }}
      >
        {tiles.map((tile, i) => (
          <KpiTile key={tile.label} {...tile} isFirst={i === 0} />
        ))}
      </Stack>
    </Card>
  );
};

export default GroupedKpiCard;
